import { MAP_IDS } from "@69ly/maps";
import { canManageTeam, TeamName, type TeamDetail } from "@69ly/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, NavLink, useLocation, useNavigate } from "react-router";
import { AimForm } from "../components/AimForm.tsx";
import { Icon } from "../components/Icon.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { MyDiscordCard, TeamDiscordCard } from "../components/TeamDiscordCard.tsx";
import { TeamFaceitCard } from "../components/TeamFaceitCard.tsx";
import { useCalloutEdits } from "../lib/callouts.ts";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useLeaderboard } from "../lib/leaderboard-queries.ts";
import { useLiveSetup } from "../lib/live.ts";
import { useMapMeshes } from "../lib/map-meshes.ts";
import { useAccessList, useDeleteTeam, useLeaveTeam, useMe, useRenameTeam, useTeam } from "../lib/queries.ts";
import { InviteCard } from "./Team.tsx";

export type SettingsSection = "team" | "account" | "admin";
const PATH: Record<SettingsSection, string> = { team: "/settings/team", account: "/settings/account", admin: "/settings/admin" };

/**
 * Settings (0.23.0): what is about managing things, out of the Team page.
 * `Team` (owner and captains edit, members read), `My account`, and `Admin`
 * for 69LY admins. A left rail on desktop, a segmented picker on phones.
 */
export function SettingsPage({ section }: { section?: SettingsSection }) {
  const { t } = useTranslation();
  const { team } = useCurrentTeam();
  const me = useMe().data;
  const isAdmin = !!me?.isAdmin;
  if (!section) return <Navigate to={team ? PATH.team : PATH.account} replace />;
  if (section === "admin" && me && !isAdmin) return <Navigate to={PATH.account} replace />;
  const sections: SettingsSection[] = ["team", "account", ...(isAdmin ? (["admin"] as const) : [])];

  return (
    <div className="page settings-page">
      <div className="page-head">
        <div>
          <p className="eyebrow">{t("settings.eyebrow")}</p>
          <h1 className="page-title">{section === "team" ? (team?.name ?? t("settings.team")) : t(`settings.${section}`)}</h1>
        </div>
      </div>
      <div className="segmented settings-pick" role="group" aria-label={t("settings.sections")}>
        {sections.map((s) => (
          <NavLink key={s} to={PATH[s]} className={({ isActive }) => `seg${isActive ? " is-active" : ""}`} aria-current={s === section ? "page" : undefined}>
            {t(`settings.${s}`)}
          </NavLink>
        ))}
      </div>
      <div className="settings">
        <nav className="settings-rail" aria-label={t("settings.sections")}>
          {sections.map((s) => (
            <NavLink key={s} to={PATH[s]} className={({ isActive }) => (isActive ? "is-active" : undefined)} aria-current={s === section ? "page" : undefined}>
              <span>{t(`settings.${s}`)}</span>
              <span className="muted small">{t(`settings.${s}Sub`)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="settings-body">
          {section === "team" && (team ? <TeamSettings teamId={team.id} /> : <NoTeam />)}
          {section === "account" && <AccountSettings />}
          {section === "admin" && <AdminSettings />}
        </div>
      </div>
    </div>
  );
}

function NoTeam() {
  const { t } = useTranslation();
  return (
    <section className="card">
      <h2 className="section-title">{t("team.noTeamTitle")}</h2>
      <p className="muted">{t("team.noTeamIntro")}</p>
      <Link to="/setup" className="btn btn-primary">
        {t("team.setUp")}
      </Link>
    </section>
  );
}

// ---------------------------------------------------------------- Team

function TeamSettings({ teamId }: { teamId: string }) {
  const { t } = useTranslation();
  const detail = useTeam(teamId);
  const me = useMe().data?.user;
  if (detail.isPending) return <Loading />;
  if (detail.error) return <ErrorState message={errorText(t, detail.error)} onRetry={() => void detail.refetch()} />;
  const team = detail.data;
  const manager = canManageTeam(team.myRole);
  return (
    <>
      <TeamNameCard team={team} canEdit={manager} />
      {manager ? <InviteCard team={team} /> : <ReadOnlyNote />}
      <TeamFaceitCard teamId={team.id} />
      <TeamDiscordCard teamId={team.id} meId={me?.id} />
      <DangerZone team={team} />
    </>
  );
}

function ReadOnlyNote() {
  const { t } = useTranslation();
  return <p className="muted small">{t("settings.teamReadOnly")}</p>;
}

function TeamNameCard({ team, canEdit }: { team: TeamDetail; canEdit: boolean }) {
  const { t } = useTranslation();
  const rename = useRenameTeam(team.id);
  const [name, setName] = useState(team.name);
  useEffect(() => setName(team.name), [team.name]);
  const check = TeamName.safeParse(name);
  const changed = name.trim() !== team.name;
  return (
    <section className="card" aria-labelledby="team-name-title">
      <h2 id="team-name-title" className="eyebrow">
        {t("settings.team")}
      </h2>
      {canEdit ? (
        <form
          className="settings-name"
          onSubmit={(e) => {
            e.preventDefault();
            if (check.success && changed) rename.mutate(check.data);
          }}
        >
          <label className="field">
            <span>{t("settings.teamName")}</span>
            <input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} aria-invalid={!check.success && name !== "" ? true : undefined} />
          </label>
          <div className="row">
            <button type="submit" className="btn btn-primary btn-small" disabled={!check.success || !changed || rename.isPending}>
              {t("common.save")}
            </button>
            <span className="muted small">{rename.isSuccess && !changed ? t("settings.teamRenamed") : t("settings.teamNameHelp")}</span>
          </div>
          {!check.success && name !== "" && <p className="field-error small">{check.error.issues[0]?.message}</p>}
          {rename.error && <p className="field-error small">{errorText(t, rename.error)}</p>}
        </form>
      ) : (
        <dl className="kv">
          <dt>{t("settings.teamName")}</dt>
          <dd>{team.name}</dd>
          <dt>{t("settings.yourRole")}</dt>
          <dd>{t(`team.roles.${team.myRole}`)}</dd>
        </dl>
      )}
    </section>
  );
}

function DangerZone({ team }: { team: TeamDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const leave = useLeaveTeam(team.id);
  const del = useDeleteTeam(team.id);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const last = team.members.length === 1;
  const owner = team.myRole === "owner";
  return (
    <section className="card danger-zone" aria-labelledby="danger-title">
      <h2 id="danger-title" className="eyebrow">
        {t("settings.dangerZone")}
      </h2>
      <p className="muted small">{owner ? t("settings.dangerHelpOwner") : t("settings.dangerHelp")}</p>
      <div className="row">
        <button
          type="button"
          className="btn btn-secondary btn-danger"
          disabled={leave.isPending}
          onClick={() => {
            if (owner && !last) {
              window.alert(t("team.ownerMustTransfer"));
              return;
            }
            const message = last ? t("team.confirmLeaveLast", { team: team.name }) : t("team.confirmLeave", { team: team.name });
            if (window.confirm(message)) leave.mutate(undefined, { onSuccess: () => navigate("/", { replace: true }) });
          }}
        >
          {t("team.leave")}
        </button>
        {owner && !confirming && (
          <button type="button" className="btn btn-secondary btn-danger" onClick={() => setConfirming(true)}>
            {t("settings.deleteTeam")}
          </button>
        )}
      </div>
      {owner && confirming && (
        <form
          className="settings-delete"
          onSubmit={(e) => {
            e.preventDefault();
            if (typed.trim() === team.name) del.mutate(undefined, { onSuccess: () => navigate("/", { replace: true }) });
          }}
        >
          <p className="small">{t("settings.deleteTeamConfirm", { team: team.name })}</p>
          <label className="field">
            <span>{t("settings.deleteTeamType", { team: team.name })}</span>
            <input value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
          </label>
          <div className="row">
            <button type="submit" className="btn btn-danger" disabled={typed.trim() !== team.name || del.isPending}>
              {t("settings.deleteTeamNow")}
            </button>
            <button type="button" className="btn btn-quiet" onClick={() => setConfirming(false)}>
              {t("common.cancel")}
            </button>
          </div>
        </form>
      )}
      {(leave.error || del.error) && <p className="field-error small">{errorText(t, leave.error ?? del.error)}</p>}
    </section>
  );
}

// ---------------------------------------------------------------- My account

function AccountSettings() {
  const { t } = useTranslation();
  const { scope } = useCurrentTeam();
  const board = useLeaderboard(scope);
  const me = board.data?.members.find((m) => m.me);
  const saved = me ? board.data?.aim[me.id] : undefined;
  const location = useLocation();
  useEffect(() => {
    if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [location.hash, board.data]);
  return (
    <>
      <MyDiscordCard />
      <section className="card" id="aim" aria-labelledby="aim-title">
        <h2 id="aim-title" className="eyebrow">
          {t("settings.aim")}
        </h2>
        <p className="muted small">{t("settings.aimHelp")}</p>
        {board.isPending ? <Loading /> : <AimForm key={JSON.stringify(saved)} saved={saved ?? { dpi: null, sensitivity: null }} bare />}
      </section>
      <LiveCard />
    </>
  );
}

function LiveCard() {
  const { t } = useTranslation();
  const setup = useLiveSetup(false);
  const data = setup.data;
  return (
    <section className="card" id="live" aria-labelledby="live-title">
      <h2 id="live-title" className="eyebrow">
        {t("nav.live")}
      </h2>
      {data && (
        <div className={`live-banner ${data.otherAccount ? "is-warn" : data.lastSeenAt ? "is-ok" : "is-off"}`} role="status">
          <span className="live-dot" aria-hidden="true" />
          <span>{data.otherAccount ? t("live.otherAccount") : data.lastSeenAt ? t("live.lastSeen", { when: new Date(data.lastSeenAt).toLocaleString() }) : t("liveSetup.waiting")}</span>
        </div>
      )}
      <p className="muted small">{t("settings.liveHelp")}</p>
      <div className="row">
        <Link to="/live/setup" className="btn btn-secondary btn-small">
          {t("settings.liveSetup")}
        </Link>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- Admin

function AdminSettings() {
  const { t } = useTranslation();
  const access = useAccessList();
  const callouts = useCalloutEdits();
  const meshes = useMapMeshes();
  const allowed = access.data?.players.filter((p) => p.access === "allowed").length;
  const hidden = callouts.data ? Object.values(callouts.data.maps).reduce((n, edits) => n + edits.filter((e) => e.hidden).length, 0) : undefined;
  const edited = callouts.data ? Object.keys(callouts.data.updatedAt).length : undefined;
  const withMesh = meshes.data ? new Set(meshes.data.map((m) => m.map)).size : undefined;
  const tile = (key: string, big: string, sub: string, to: string, icon: "shield" | "tag" | "map") => (
    <section className="card admin-card" key={key}>
      <h2 className="eyebrow">
        <Icon name={icon} size={14} /> {t(`nav.${key}`)}
      </h2>
      <span className="admin-big">{big}</span>
      <span className="muted small">{sub}</span>
      <Link to={to} className="btn btn-secondary btn-small">
        {t("settings.open", { page: t(`nav.${key}`) })}
      </Link>
    </section>
  );
  return (
    <>
      <p className="muted small">{t("settings.adminHelp")}</p>
      <div className="admin-cards">
        {tile("access", allowed === undefined ? "…" : String(allowed), access.data ? t("settings.adminAccess", { mode: access.data.mode }) : "", "/access", "shield")}
        {tile("callouts", hidden === undefined ? "…" : String(hidden), edited === undefined ? "" : t("settings.adminCallouts", { count: edited }), "/admin/callouts", "tag")}
        {tile("meshes", withMesh === undefined ? "…" : `${withMesh} / ${MAP_IDS.length}`, t("settings.adminMeshes"), "/admin/maps", "map")}
      </div>
    </>
  );
}
